import React, { useState, useEffect } from 'react';
import './Results.scss';
import {
  Spin, Row, Col, Card, Form, DatePicker,
  Input, Button, Collapse, Modal, Select, Checkbox,
  Tooltip, notification,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import {
  DownloadOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import moment, { Moment } from 'moment/moment';
import { useNavigate } from 'react-router-dom';
import {
  ServiceFromList, ServiceTypeFromList, ServiceSubTypeFromList,
} from '../../../sharedTypes/ServiceTypes';
import { CommonResults, FileImportResult } from '../../../sharedTypes/ResultsImportTypes';
import { downloadFile } from '../../../utils/helpers';
import PdfVisualizer from './pdfVisualizer/PdfVisualizer';
import {
  get, patch, isSuperAgentError,
  ResponseWrapper, RequestWrapper,
} from '../../../dataSource/requests';
import geofertImg from '../../../assets/images/001-agriculture.svg';
import sprayDroneImg from '../../../assets/images/002-drone.svg';
import smartFarmImg from '../../../assets/images/003-smart-farm.svg';
import satelliteImg from '../../../assets/images/004-satellite.svg';
import vantImg from '../../../assets/images/005-drone-1.svg';
import posVendaImg from '../../../assets/images/006-posvendas.svg';
import imgPdf from '../../../assets/images/icon-pdf.svg';
import imgZip from '../../../assets/images/icon-zip.svg';
import imgKml from '../../../assets/images/icon-kml.png';
import imgKmz from '../../../assets/images/icon-kmz.png';
import imgDocx from '../../../assets/images/icon-docx.png';
import { getUserType } from '../../../utils/users';
import { backendResponseCodes } from '../../../utils/responseMessages';

const Results = (): JSX.Element => {
  interface PDFList {
    [propertyName: string]: Array<FileImportResult>;
  }

  interface PermissionList {
    [propertyName: string]: boolean;
  }

  interface ResultsForm {
    allCheckbox: boolean | undefined;
    allSubCheckbox: boolean | undefined;
    cooperativeName: string;
    intervalFilter: Array<Moment>;
    selectedService: number;
    type: number;
    subType: number;
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const [userType] = useState<string | null>(getUserType());
  const [loading, setLoading] = useState<boolean>(false);
  const [services, setServices] = useState<Array<ServiceFromList>>([]);
  const [selectedService, setSelectedService] = useState<number | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<ServiceTypeFromList | undefined>(undefined);
  const [types, setTypes] = useState<Array<ServiceTypeFromList>>([]);
  const [subTypes, setSubTypes] = useState<Array<ServiceSubTypeFromList>>([]);
  const [maxPeriodOfDaysFilter, setMaxPeriodOfDaysFilter] = useState<number>(30);
  const [resultList, setResultList] = useState<Array<CommonResults>>([]);
  const [pdfLists, setPdfLists] = useState<PDFList>([]);
  const [permissionList, setPermissionList] = useState<PermissionList>([]);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [allTypes, setAllTypes] = useState<boolean>(false);
  const [allSubTypes, setAllSubTypes] = useState<boolean>(false);
  const [urls, setUrls] = useState([]);

  const getIconByName = (namee: string): any => {
    switch (namee) {
      case 'pdf':
        return imgPdf;

      case 'kml':
        return imgKml;

      case 'kmz':
        return imgKmz;

      case 'zip':
        return imgZip;

      case 'docx':
        return imgDocx;

      case 'doc':
        return imgDocx;

      default:
        return imgZip;
    }
  };

  const validateDateRange = (_rule: any, value: Array<Moment>): Promise<void> => {
    if (!maxPeriodOfDaysFilter) {
      return Promise.resolve();
    }

    if (!value?.[0] && !value?.[1]) {
      return Promise.resolve();
    }

    if (!value?.[0] || !value?.[1]) {
      return Promise.reject(new Error('Por favor preencha o período inicial e final.'));
    }

    if (
      Math.abs(moment(value?.[0]).diff(moment(value?.[1]), 'days')) > maxPeriodOfDaysFilter
    ) {
      return Promise.reject(new Error(`A busca é limitada a um intervalo de ${maxPeriodOfDaysFilter} dias`));
    }

    return Promise.resolve();
  };

  const getServiceImg = (id: number): any => {
    switch (id) {
      case 1:
        return geofertImg;

      case 2:
        return sprayDroneImg;

      case 3:
        return vantImg;

      case 4:
        return satelliteImg;

      case 5:
        return smartFarmImg;

      case 6:
        return posVendaImg;

      default:
        return geofertImg;
    }
  };
  const elementOption = (v): JSX.Element => (
    <Select.Option key={v.id} value={v.id}>
      {v.name}
    </Select.Option>
  );

  const getServices = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    setLoading(true);

    try {
      const serviceListAllReqRef: RequestWrapper = get({
        url: '/service/listAll',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await serviceListAllReqRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5000):
        // "Services listed successfully", 5000
        setServices(response?.body.services);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar os serviços, por favor '
            + 'tente novamente mais tarde',
        });
        break;
    }

    setLoading(false);
  };

  const changeCheck = (v: CheckboxChangeEvent | any): void => {
    setAllTypes(v.target.checked);
  };

  const changeCheckSub = (v: CheckboxChangeEvent | any): void => {
    setAllSubTypes(v.target.checked);
  };

  const downloadFileLocal = async (
    v: FileImportResult | null,
    _url: string,
    name: string,
  ): Promise<void> => {
    let url = `/file/downloadFile/${v?.fileAttachmentId}`;

    if (getUserType() === 'COOPERATIVE') {
      url = `/file/customer/downloadFile/${v?.fileAttachmentId}`;
    }

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const downloadFileReqRef: RequestWrapper = get({
        url: `${_url || url}`,

        responseType: 'blob',

        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await downloadFileReqRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200): {
        let alternateFilename;

        if (name != null) {
          alternateFilename = `${name}.zip`;
        } else {
          alternateFilename = 'Todos os arquivos.zip';
        }

        downloadFile(
          response?.body,
          v?.fileAttachmentName != null
            ? v?.fileAttachmentName : (alternateFilename),
        );
      } break;

      case (responseError?.body.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body.code === -9002):
        // "File not found", -9002
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .FileNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar o anexo, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }
  };

  const getResults = async (values: ResultsForm): Promise<ResponseWrapper | null | undefined> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    setLoading(true);

    const params = {
      serviceId: values?.selectedService,
      periodStart: values?.intervalFilter?.[0],
      periodEnd: values?.intervalFilter?.[1],
      customerName: values?.cooperativeName,
      serviceTypeId: allTypes ? null : values?.type,
      serviceSubTypeId: allTypes || allSubTypes ? null : values?.subType,
    };

    let url = '/file/coopercitrus/listAllFileImportResult';

    if (getUserType() === 'COOPERATIVE') {
      url = '/file/customer/listAllFileImportResult';
    }

    try {
      const listAllFileImportResultReqRef: RequestWrapper = patch({
        url,
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await listAllFileImportResultReqRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);

    switch (true) {
      case (response?.body?.code === 9019):
      case (response?.body?.code === 9018):
        // "List all file results coopercitrus successfully", 9019
        // "List all file results customer successfully", 9018
        return response;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        return null;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        return null;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        return null;

      case (responseError?.body?.code === -9019):
        // "Invalid file import result filters", -9019
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.File
            .InvalidFileImportResultFilters.description.pt_br,
        });
        return null;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        return null;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar os resultados, por favor '
            + 'tente novamente mais tarde.',
        });
        return null;
    }
  };

  const getMaxPeriod = async (): Promise<void> => {
    let response1: ResponseWrapper | undefined;
    let responseError1: ResponseWrapper | undefined;
    let genericError1: Error | undefined;

    try {
      const serviceGetMaxPeriodOfDaysFilterReqRef: RequestWrapper = get({
        url: '/service/getMaxPeriodOfDaysFilter',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response1 = await serviceGetMaxPeriodOfDaysFilterReqRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError1 = error.response;
      } else {
        genericError1 = error as Error;
      }
    }

    switch (true) {
      case (response1?.body?.code === 5012):
        // "Max period of days filter got successfully", 5012
        setMaxPeriodOfDaysFilter(response1?.body?.maxPeriodOfDaysFilter);
        break;

      case (responseError1?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError1?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (genericError1?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError1?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao carregar o período máximo de dias, '
            + 'por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    getServices();
    getMaxPeriod();

    return () => {
      setLoading(false);
    };
  }, []);

  const setFileListsWithFlag = (resList: Array<CommonResults>): void => {
    const newPdfList: PDFList = [];

    const newPermissionsList: PermissionList = [];

    for (const resKey in resList) {
      newPdfList[resList[resKey].landName] = [];
      newPermissionsList[resList[resKey].landName] = false;

      for (const fileKey in resList[resKey]?.listAllFileImportResult) {
        if (resList[resKey]?.listAllFileImportResult[fileKey].fileAttachmentType === 'pdf') {
          newPdfList[resList[resKey].landName][resList[resKey]
            .listAllFileImportResult[fileKey].id] = {
            file: resList[resKey].listAllFileImportResult[fileKey],
            selected: false,
          };
        }
      }
    }


    setPdfLists(newPdfList);
    setPermissionList(newPermissionsList);
  };

  useEffect(() => {
    setFileListsWithFlag(resultList);
  }, [resultList]);

  useEffect(() => {
    const newPermiteds = [];

    for (const l in pdfLists) {
      let selecteds = 0;

      for (const f in pdfLists[l]) {
        if (pdfLists[l][f].selected) {
          selecteds += 1;
        }
      }

      newPermiteds[l] = selecteds >= 2;
    }

    setPermissionList(newPermiteds);
  }, [pdfLists]);

  const setList = (v): void => {
    setResultList(v);
  };

  const updateSelect = (value: number): void => {
    const type = value;
    const selected = types.find((v) => v.id === type);
    setSelectedType(selected);
    setSubTypes(selected?.subtypes);
  };

  const clearType = (): void => {
    setSelectedType(undefined);
    formInstance.setFieldsValue({ type: '' });
    formInstance.setFieldsValue({ subType: '' });
  };

  const selectService = (v: number): void => {
    setSelectedService(v);
    formInstance.setFieldsValue({ selectedService: v });
    const service = services.find((s) => s.id === v);
    setTypes(service?.types || []);
    clearType();
  };

  const selectPdf = (vId, fId): void => {
    const list = [];

    for (const pdfKey in pdfLists) {
      list[pdfKey] = pdfLists[pdfKey];
    }

    let moreThanTwo = false;

    for (const listKey in list) {
      let selected = 0;

      for (const f in list[listKey]) {
        if (list[listKey][f].selected) {
          selected += 1;
        }
      }

      if (vId.landName === listKey && !list[vId.landName][fId.id].selected) {
        selected += 1;
      }

      if (selected > 2) {
        moreThanTwo = true;
      }
    }

    if (!moreThanTwo) {
      list[vId.landName][fId.id]
        .selected = !list[vId.landName][fId.id].selected;
      setPdfLists(list);
    }
  };

  const cleanFilterFields = (): void => {
    clearType();

    formInstance.setFieldsValue({
      selectedService: undefined,
      cooperativeName: '',
      allSubCheckbox: false,
      allCheckbox: false,
    });

    changeCheckSub({
      target: {
        checked: false,
      },
    });

    changeCheck({
      target: {
        checked: false,
      },
    });

    setSelectedService(undefined);
    setTypes([]);
    setList([]);
  };

  const searchResults = async (): Promise<void> => {
    let values;

    setLoading(true);

    try {
      values = await formInstance.validateFields();
    } catch (error: unknown) {
      console.log(error);
      setLoading(false);
      return;
    }

    values = formInstance.getFieldsValue();

    const newResults = await getResults(values);

    setList(newResults?.body?.fileAttachments);
    setLoading(false);
  };
  const keyDownEvent = (eventt: any, v: any, f: any): void => {
    if (eventt.key === 'Enter') {
      selectPdf(v, f);
    }
  };

  return (
    <div className="results">
      <Spin tip="Carregando..." spinning={loading}>
        <div className="content">
          <Row>
            <Col>
              <Row>
                <span className="page-title">Resultados</span>
              </Row>
              <Row>
                <Form
                  form={formInstance}
                >
                  <Col>
                    <Form.Item
                      className="form-item"
                      name="selectedService"
                      label="Selecione o serviço"
                      labelCol={{ span: 24 }}
                      rules={[
                        { required: true, message: 'Por favor, selecione um serviço' },
                      ]}
                    >
                      <Row>
                        {
                          services.filter((file) => file.name !== 'Pós-vendas'
                            && file.name !== 'Spray Drone'
                            && file.name !== 'Aplicação de corretivos',
                          ).map((v) => (
                            <Card
                              key={v.id}
                              hoverable
                              className={`${selectedService === v.id ? 'service-card selected' : 'service-card'}`}
                              onClick={
                                (): void => {
                                  selectService(v.id);
                                }
                              }
                            >
                              <img src={getServiceImg(v.id)} alt="" />
                              <div className="card-title">{v.name}</div>
                            </Card>
                          ))
                        }
                      </Row>
                      {
                        types.length > 0
                        && selectedService != undefined
                        && (
                          <Row gutter={24} align="middle" className="rowTipo">
                            <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                              <Form.Item
                                label={<span className="select-property-text">Tipo</span>}
                                name="type"
                              >
                                <Select
                                  onChange={
                                    (value, type): void => {
                                      updateSelect(value, type);
                                    }
                                  }
                                  placeholder="Selecione o tipo"
                                  disabled={allTypes}
                                >
                                  {
                                    types.map((v) => elementOption(v))
                                  }
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                              <Form.Item
                                name="allCheckbox"
                              >
                                <Row>
                                  <Checkbox
                                    onChange={
                                      (v: CheckboxChangeEvent): void => {
                                        changeCheck(v);
                                      }
                                    }
                                  />
                                  <Col style={{ paddingLeft: 10 }}>
                                    Todos
                                  </Col>
                                </Row>
                              </Form.Item>
                            </Col>
                          </Row>
                        )
                      }
                      {
                        subTypes.length > 0
                        && selectedType != null
                        && (
                          <Row gutter={24}>
                            <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                              <Form.Item
                                label={<span className="select-property-text">Subtipo</span>}
                                name="subType"
                              >
                                <Select
                                  disabled={allTypes || allSubTypes}
                                  placeholder="Selecione o Subtipo"
                                >
                                  {
                                    subTypes.map((v) => elementOption(v))
                                  }
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                              <Form.Item
                                name="allSubCheckbox"
                              >
                                <Row>
                                  <Checkbox
                                    onChange={
                                      (v: CheckboxChangeEvent): void => {
                                        changeCheckSub(v);
                                      }
                                    }
                                  />
                                  <Col style={{ paddingLeft: 10 }}>
                                    Todos
                                  </Col>
                                </Row>
                              </Form.Item>
                            </Col>
                          </Row>
                        )
                      }
                    </Form.Item>
                    <Row>
                      <Form.Item
                        label="Período"
                        name="intervalFilter"
                        className="ListServices__IntervalFilterFormItem form-item scheduleField"
                        rules={[{ validator: validateDateRange }]}
                        initialValue={[moment().subtract(30, 'day'), moment()]}
                        labelCol={{ span: 24 }}
                      >
                        <DatePicker.RangePicker
                          className="ListServices__IntervalFilterDatepicker"
                          style={{ width: '100%' }}
                          placeholder={['Início', 'Fim']}
                          format="DD/MM/YYYY"
                        />
                      </Form.Item>
                      {userType === 'COOPERCITRUS' && (
                        <Form.Item
                          className="form-item"
                          name="cooperativeName"
                          label="Nome do Cooperado"
                          labelCol={{ span: 24 }}
                        >
                          <Input></Input>
                        </Form.Item>
                      )}
                    </Row>
                    <Row>
                      <Form.Item
                        className="form-item"
                        name="submitButton"
                        labelCol={{ span: 24 }}
                      >
                        <Button
                          block
                          className="ListServiceEstimates__ResetButton btn-clean"
                          onClick={
                            (): void => {
                              cleanFilterFields();
                            }
                          }
                          htmlType="reset"
                        >
                          <FilterRemoveIcon
                            className="ListServiceEstimates__ResetButtonIcon"
                          />
                          <span
                            className="ListServiceEstimates__ResetButtonText"
                          >
                            Limpar
                          </span>
                        </Button>
                      </Form.Item>
                      <Form.Item
                        className="form-item"
                        name="submitButton"
                        labelCol={{ span: 24 }}
                      >
                        <Button
                          className="search"
                          type="primary"
                          onClick={
                            (): void => {
                              searchResults();
                            }
                          }
                        >
                          Filtrar
                        </Button>
                      </Form.Item>
                    </Row>
                  </Col>
                </Form>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col>
              {
                resultList?.map((v) => (
                  <Row justify="center">
                    <Collapse defaultActiveKey={['0']} className="Panels">
                      <Collapse.Panel header={`${v.landName}`} key={v.landName}>
                        {
                          v.listAllFileImportResult.filter((file) => file.fileAttachmentType === 'pdf').length > 0 && (
                            <div>
                              <Row className="section-title">Arquivos PDF</Row>
                              <Row className="rowPdf">
                                {
                                  v.listAllFileImportResult.filter((file) => file.fileAttachmentType === 'pdf').map((f) => (
                                    <div id="container">
                                      <Tooltip
                                        placement="topLeft"
                                        title={`Nome: ${f.fileAttachmentName}
                                        ${f?.fileAttachmentNote != null
                                            ? 'Observação: '
                                            : ''
                                          }
                                        ${f?.fileAttachmentNote != null
                                            ? f.fileAttachmentNote
                                            : ''
                                          }`}
                                      >
                                        <Card
                                          key={f.id}
                                          className={`${pdfLists[v.landName] != null
                                            && pdfLists[v.landName][f.id] != null
                                            && pdfLists[v.landName][f.id].selected ? 'file-card selected' : 'file-card'}`}
                                        >
                                          <img
                                            className="selectFile"
                                            src={getIconByName(f.fileAttachmentType)}
                                            alt=""
                                            onClick={
                                              (): void => {
                                                selectPdf(v, f);
                                              }
                                            }
                                            role="button"
                                            aria-label="pdf"
                                            tabIndex={0}
                                            onKeyDown={
                                              (eventt): void => {
                                                keyDownEvent(eventt, v, f);
                                              }
                                            }
                                          />

                                          <div className="divInfo">
                                            <div
                                              onClick={
                                                (): void => {
                                                  selectPdf(v, f);
                                                }
                                              }
                                              className="file-title selectFile"
                                              role="button"
                                              aria-label="pdf"
                                              tabIndex={0}
                                              onKeyDown={
                                                (eventt): void => {
                                                  keyDownEvent(eventt, v, f);
                                                }
                                              }
                                            >
                                              {f.fileAttachmentName}
                                            </div>
                                            <div
                                              onClick={
                                                (): void => {
                                                  selectPdf(v, f);
                                                }
                                              }
                                              className="file-title selectFile"
                                              role="button"
                                              aria-label="pdf"
                                              tabIndex={0}
                                              onKeyDown={
                                                (eventt): void => {
                                                  keyDownEvent(eventt, v, f);
                                                }
                                              }
                                            >
                                              {`Id do serviço: ${f.serviceRequestId}`}
                                              <br />
                                              {
                                                `Data: ${moment(f.fileAttachmentRegistrationDate)
                                                  .startOf('day')
                                                  .format('DD/MM/YYYY')
                                                }`
                                              }
                                              <br />
                                              {`Talhão: ${f.landFieldName}`}
                                            </div>
                                          </div>
                                          <Row justify="center">
                                            <div className="downloadIcon">
                                              <DownloadOutlined
                                                onClick={
                                                  (): void => {
                                                    downloadFileLocal(f, null, null);
                                                  }
                                                }
                                              />
                                            </div>
                                            <div className="downloadIcon">
                                              <SearchOutlined
                                                onClick={
                                                  async (): Promise<void> => {
                                                    const arrayIds = [f.fileAttachmentId];
                                                    const arrayUrls = [];

                                                    for (const k in arrayIds) {
                                                      let url = `/file/downloadFile/${arrayIds[k]}`;

                                                      if (getUserType() === 'COOPERATIVE') {
                                                        url = `/file/customer/downloadFile/${arrayIds[k]}`;
                                                      }

                                                      let response: ResponseWrapper | undefined;
                                                      let responseError: ResponseWrapper | undefined;
                                                      let genericError: Error | undefined;

                                                      try {
                                                        const downloadFile2ReqRef = get({
                                                          url: `${url}`,

                                                          responseType: 'blob',

                                                          headers: {
                                                            'Content-Type': 'application/json',
                                                            Accept: '*/*',
                                                          },

                                                          frontConfigs: {
                                                            navigateFunction: navigate,
                                                          },
                                                        });

                                                        response = await downloadFile2ReqRef;
                                                      } catch (error: unknown) {
                                                        if (isSuperAgentError(error)) {
                                                          responseError = error.response;
                                                        } else {
                                                          genericError = error as Error;
                                                        }
                                                      }

                                                      switch (true) {
                                                        case (response?.statusCode === 200): {
                                                          const blob = new Blob([response?.body]);
                                                          const newUrl = window.URL.createObjectURL(blob);
                                                          arrayUrls.push(newUrl);
                                                        } break;

                                                        case (responseError?.body?.code === -4001):
                                                          // "You must be authenticated to perform this operation", -4001
                                                          break;

                                                        case (responseError?.body?.code === -4003):
                                                          // "You do not have permission to perform this operation", -4003
                                                          notification.error({
                                                            message: 'Erro',
                                                            description: backendResponseCodes.Error
                                                              .General.Forbidden.description.pt_br,
                                                          });
                                                          break;

                                                        case (responseError?.body?.code === -9002):
                                                          // "File not found", -9002
                                                          notification.error({
                                                            message: 'Erro',
                                                            description: backendResponseCodes.Error
                                                              .File.FileNotFound.description.pt_br,
                                                          });
                                                          break;

                                                        case (genericError?.message?.includes('has been aborted')):
                                                          // dont show anything when request is aborted
                                                          break;

                                                        default:
                                                          notification.error({
                                                            message: 'Erro',
                                                            description: 'Desculpe, houve um erro ao baixar o anexo, '
                                                              + 'por favor tente novamente mais tarde.',
                                                          });
                                                          break;
                                                      }
                                                    }

                                                    setUrls([arrayUrls[0]]);
                                                    setVisibleModal(true);
                                                  }
                                                }
                                              />
                                            </div>
                                          </Row>
                                        </Card>
                                      </Tooltip>
                                    </div>
                                  ))
                                }
                              </Row>
                            </div>
                          )
                        }
                        {
                          pdfLists[v.landName]?.length > 0 && (
                            <Row style={{ paddingBottom: 10 }}>
                              <Button
                                disabled={!permissionList[v.landName]}
                                onClick={
                                  permissionList[v.landName]
                                    ? async (): Promise<void> => {
                                      const arrayIds = [];

                                      for (const k in pdfLists[v.landName]) {
                                        if (pdfLists[v.landName][k].selected) {
                                          arrayIds.push(
                                            pdfLists[v.landName][k].file.fileAttachmentId,
                                          );
                                        }
                                      }

                                      const arrayUls = [];

                                      for (const k in arrayIds) {
                                        let url = `/file/downloadFile/${arrayIds[k]}`;

                                        if (getUserType() === 'COOPERATIVE') {
                                          url = `/file/customer/downloadFile/${arrayIds[k]}`;
                                        }

                                        let response: ResponseWrapper | undefined;
                                        let responseError: ResponseWrapper | undefined;
                                        let genericError: Error | undefined;

                                        try {
                                          const downloadFile3ReqRef: RequestWrapper = get({
                                            url: `${url}`,

                                            responseType: 'blob',

                                            headers: {
                                              'Content-Type': 'application/json',
                                              Accept: '*/*',
                                            },

                                            frontConfigs: {
                                              navigateFunction: navigate,
                                            },
                                          });

                                          response = await downloadFile3ReqRef;
                                        } catch (error: unknown) {
                                          if (isSuperAgentError(error)) {
                                            responseError = error.response;
                                          } else {
                                            genericError = error as Error;
                                          }
                                        }

                                        switch (true) {
                                          case (response?.statusCode === 200): {
                                            const blob = new Blob([response.body]);
                                            const blobUrl = window.URL.createObjectURL(blob);
                                            arrayUls.push(blobUrl);
                                          } break;

                                          case (responseError?.body?.code === -4001):
                                            /* "You must be authenticated to perform
                                            this operation", -4001 */
                                            break;

                                          case (responseError?.body?.code === -4003):
                                            /* "You do not have permission to perform
                                            this operation", -4003 */
                                            notification.error({
                                              message: 'Erro',
                                              description: backendResponseCodes.Error
                                                .General.Forbidden.description.pt_br,
                                            });
                                            break;

                                          case (responseError?.body?.code === -9002):
                                            // "File not found", -9002
                                            notification.error({
                                              message: 'Erro',
                                              description: backendResponseCodes.Error
                                                .File.FileNotFound.description.pt_br,
                                            });
                                            break;

                                          case (
                                            genericError
                                              ?.message
                                              ?.includes('has been aborted')
                                            ):
                                            // dont show anything when request is aborted
                                            break;

                                          default:
                                            notification.error({
                                              message: 'Erro',
                                              description: 'Desculpe, houve um erro ao baixar o anexo, '
                                                + 'por favor tente novamente mais tarde.',
                                            });
                                            break;
                                        }
                                      }

                                      setUrls([arrayUls[0], arrayUls[1]]);
                                      setVisibleModal(true);
                                    }
                                    : (): void => { }
                                }
                              >
                                Comparar resultados
                              </Button>
                            </Row>
                          )
                        }
                        {
                          v?.listAllFileImportResult?.filter(
                            (file) => file.fileAttachmentType === 'kml'
                              || file.fileAttachmentType === 'kmz',
                          ).length > 0 && (
                            <div>
                              <Row className="section-title padded">Arquivos de Mapas</Row>
                              <Row style={{ paddingBottom: 10 }}>
                                {
                                  v?.listAllFileImportResult?.filter(
                                    (file) => file.fileAttachmentType === 'kml'
                                      || file.fileAttachmentType === 'kmz',
                                  )?.map((f) => (
                                    <Tooltip
                                      placement="topLeft"
                                      title={`Nome: ${f.fileAttachmentName}
                                      ${f?.fileAttachmentNote != null
                                        ? 'Observação: '
                                        : ''
                                        }
                                      ${f?.fileAttachmentNote != null
                                          ? f.fileAttachmentNote
                                          : ''
                                        }`}
                                    >
                                      <Card
                                        key={f.id}
                                        className="file-card"
                                      >
                                        <div>
                                          <img
                                            className="selectFile"
                                            src={getIconByName(f.fileAttachmentType)}
                                            alt=""
                                          />
                                          <div className="divInfo">
                                            <div
                                              className="file-title selectFile"
                                            >
                                              {f.fileAttachmentName}
                                            </div>
                                            <div
                                              className="file-title selectFile"
                                            >
                                              {`Id do serviço: ${f.serviceRequestId}`}
                                              <br />
                                              {`Talhão: ${f.landFieldName}`}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="downloadIcon">
                                          <DownloadOutlined
                                            onClick={
                                              (): void => {
                                                downloadFileLocal(f, null, null);
                                              }
                                            }
                                          />
                                        </div>
                                      </Card>
                                    </Tooltip>
                                  ))
                                }
                              </Row>
                            </div>
                          )
                        }
                        {
                          v?.listAllFileImportResult?.filter(
                            (file) => file.fileAttachmentType !== 'kml'
                              && file.fileAttachmentType !== 'kmz'
                              && file.fileAttachmentType !== 'pdf',
                          ).length > 0 && (
                            <div>
                              <Row className="section-title padded">Outros tipos de arquivos</Row>
                              <Row style={{ paddingBottom: 10 }}>
                                {
                                  v?.listAllFileImportResult?.filter(
                                    (file) => file.fileAttachmentType !== 'kml'
                                      && file.fileAttachmentType !== 'kmz'
                                      && file.fileAttachmentType !== 'pdf',
                                  )?.map((f) => (
                                    <Tooltip
                                      placement="topLeft"
                                      title={`Nome: ${f.fileAttachmentName}
                                      ${f?.fileAttachmentNote != null
                                        ? 'Observação: '
                                        : ''
                                        }
                                      ${f?.fileAttachmentNote != null
                                          ? f.fileAttachmentNote
                                          : ''
                                        }`}
                                    >
                                      <Card
                                        key={f.id}
                                        className="file-card"
                                      >
                                        <div>
                                          <img
                                            className="selectFile"
                                            src={getIconByName(f.fileAttachmentType)}
                                            alt=""
                                          />
                                          <div className="divInfo">
                                            <div
                                              className="file-title selectFile"
                                            >
                                              {f.fileAttachmentName}
                                            </div>
                                            <div
                                              className="file-title selectFile"
                                            >
                                              {`Id do serviço: ${f.serviceRequestId}`}
                                              <br />
                                              {`Talhão: ${f.landFieldName}`}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="downloadIcon">
                                          <DownloadOutlined
                                            onClick={
                                              (): void => {
                                                downloadFileLocal(f, null, null);
                                              }
                                            }
                                          />
                                        </div>
                                      </Card>
                                    </Tooltip>
                                  ))
                                }
                              </Row>
                            </div>
                          )
                        }
                        <Row className="tooltipAndButtonLine">
                          <Button
                            onClick={
                              (): void => {
                                let url = `/file/downloadAllFileImportResult/${v.landId}`;

                                if (getUserType() === 'COOPERATIVE') {
                                  url = `/file/customer/downloadAllFileImportResult/${v.landId}`;
                                }

                                downloadFileLocal(null, url, v.landName);
                              }
                            }
                          >
                            Baixar arquivos
                          </Button>
                          <Tooltip
                            title={
                              'Para a funcionalidade de "Baixar arquivos" '
                              + 'são consideradas todas as importações de resultados'
                              + ' vinculadas à propriedade.'
                            }
                          >
                            <InfoCircleOutlined className="tooltipInfo" />
                          </Tooltip>
                        </Row>
                      </Collapse.Panel>
                    </Collapse>
                  </Row>
                ))
              }
            </Col>
          </Row>
        </div>
      </Spin>
      <Modal
        title={<h3 style={{ margin: 0 }}>Comparação de resultados</h3>}
        visible={visibleModal}
        width={1100}
        style={{ marginTop: -90, textAlign: 'center' }}
        onCancel={
          (): void => {
            setVisibleModal(false);
          }
        }
        footer={[
          <Button
            className="btn-ok"
            type="primary"
            key="submit"
            onClick={
              (): void => {
                setVisibleModal(false);
              }
            }
          >
            Ok
          </Button>,
        ]}
      >
        {
          urls.length > 1 && (
            <Row className="pdf-modal">
              <div className="pdf-div">
                <PdfVisualizer
                  url={urls[0]}
                  className="pdf"
                >
                </PdfVisualizer>
              </div>
              <div className="pdf-div">
                <PdfVisualizer
                  url={urls[1]}
                  className="pdf"
                >
                </PdfVisualizer>
              </div>
            </Row>
          )
        }
        {
          urls.length === 1 && (
            <Row justify="center" className="pdf-modal">
              <div className="pdf-div">
                <PdfVisualizer
                  url={urls[0]}
                  className="pdf"
                >
                </PdfVisualizer>
              </div>
            </Row>
          )
        }
      </Modal>
    </div>
  );
};

Results.propTypes = {};
Results.defaultProps = {};

export default Results;
